export default function CaseStudiesSection() {
  const cases = [
    {
      title: 'Tax Liens & Mechanic\'s Liens',
      property: '4-bedroom house in East El Paso',
      situation: 'Inherited property with $18,000 in back property taxes and $12,000 in mechanic\'s liens from unpaid contractor work. The heir couldn\'t afford to pay these liens out of pocket.',
      solution: 'We purchased the property for $145,000. At closing, we paid off all $30,000 in liens directly from the proceeds. The heir walked away with $115,000 cash.',
      timeline: '12 days from first contact to closing',
      image: 'https://public.readdy.ai/ai/img_res/a24b8612e7a1a5793f19c927e9f996ec.jpg'
    },
    {
      title: 'Clouded Title with Missing Heir',
      property: '3-bedroom house in Central El Paso',
      situation: 'Three siblings inherited the property, but one moved to California 15 years ago and couldn\'t be located. The title was clouded because all three needed to sign to sell.',
      solution: 'We hired a professional heir locator and found the missing sibling in San Diego. We negotiated with all three parties and structured a deal that satisfied everyone. Closed for $168,000.',
      timeline: '45 days (including heir search)',
      image: 'https://readdy.ai/api/search-image?query=charming%20older%20home%20with%20character%20vintage%20architecture%20needing%20updates%20family%20property%20inheritance%20situation%20residential%20neighborhood%20mature%20trees%20El%20Paso&width=800&height=600&seq=titleissues013&orientation=landscape'
    },
    {
      title: 'Probate with Back Taxes',
      property: '5-bedroom house in West El Paso',
      situation: 'Property owner died without a will. Heir needed to go through probate but couldn\'t afford to pay the $22,000 in back taxes while waiting for probate to complete.',
      solution: 'We gave the heir a cash offer immediately and agreed to wait for probate to complete. Once Letters of Administration were issued, we closed in 5 days and paid all back taxes at closing. Heir received $195,000.',
      timeline: '8 months (waiting for probate) + 5 days to close',
      image: 'https://readdy.ai/api/search-image?query=large%20family%20home%20estate%20property%20with%20mature%20landscaping%20probate%20situation%20inherited%20house%20traditional%20architecture%20spacious%20property%20El%20Paso%20residential%20area&width=800&height=600&seq=titleissues014&orientation=landscape'
    },
    {
      title: 'Boundary Dispute & Encroachment',
      property: '3-bedroom house in Northeast El Paso',
      situation: 'New survey revealed the garage encroached 4 feet onto the neighbor\'s property. Traditional buyers walked away. The neighbor was demanding $15,000 to grant an easement.',
      solution: 'We purchased the property as-is for $152,000, accepting the boundary risk. After closing, we negotiated with the neighbor and resolved the encroachment for $8,000.',
      timeline: '10 days to closing',
      image: 'https://readdy.ai/api/search-image?query=house%20with%20attached%20garage%20showing%20property%20line%20issue%20fence%20and%20boundary%20markers%20visible%20survey%20stakes%20residential%20property%20with%20encroachment%20problem&width=800&height=600&seq=titleissues015&orientation=landscape'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Real <strong>Success Stories</strong> from El Paso
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            These are actual deals we've closed in El Paso. Names changed for privacy, but the situations and solutions are 100% real.
          </p>
        </div>

        <div className="space-y-12">
          {cases.map((caseStudy, index) => (
            <div key={index} className="bg-gray-50 rounded-lg shadow-xl overflow-hidden">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-80 md:h-auto">
                  <img 
                    src={caseStudy.image}
                    alt={caseStudy.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-6 left-6 bg-emerald-500 text-white px-4 py-2 rounded-lg font-bold">
                    Case Study #{index + 1}
                  </div>
                </div>
                <div className="p-8 md:p-12">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">{caseStudy.title}</h3>
                  <p className="text-lg text-emerald-600 font-semibold mb-6">{caseStudy.property}</p>
                  
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                        <i className="ri-error-warning-line text-red-600"></i>
                        The Situation:
                      </h4>
                      <p className="text-gray-700 leading-relaxed">{caseStudy.situation}</p>
                    </div>
                    
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                        <i className="ri-lightbulb-line text-emerald-600"></i>
                        Our Solution:
                      </h4>
                      <p className="text-gray-700 leading-relaxed">{caseStudy.solution}</p>
                    </div>
                    
                    <div className="bg-white border-l-4 border-emerald-500 p-4 rounded">
                      <p className="text-sm font-semibold text-gray-900 mb-1">Timeline:</p>
                      <p className="text-gray-700">{caseStudy.timeline}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-emerald-500 text-white rounded-lg p-8 md:p-12 text-center">
          <h3 className="text-3xl font-bold mb-4">Your Situation Might Be Different, But We Can Still Help</h3>
          <p className="text-xl mb-8 leading-relaxed max-w-3xl mx-auto">
            Every title issue is unique. Whether you have liens, clouded titles, probate complications, boundary disputes, or a combination of problems, we have the experience and resources to find a solution.
          </p>
          <a 
            href="tel:9152065814"
            className="inline-block bg-white text-emerald-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
          >
            Call (915) 206-5814 for Free Consultation
          </a>
        </div>
      </div>
    </section>
  );
}